import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { ArrowsHorizontal } from "@phosphor-icons/react";

const KIND_LABELS = {
    emotional_theme: "Emotional theme",
    phrase: "Phrase",
    contradiction: "Tension",
    observation: "Observation",
};

const KIND_ORDER = ["contradiction", "emotional_theme", "phrase", "observation"];

/**
 * Cross-Pollination panel — only renders on the GLOBAL briefing scope.
 * Surfaces narrative terms (themes / phrases / contradictions / observations)
 * that recur across multiple Streams. The editorial question this answers:
 * "What's not just one stream's story — what's actually crossing?"
 */
export default function CrossPollinationPanel({ sinceDays = 30 }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        api.get("/dashboard/cross-pollination", { params: { since_days: sinceDays, min_streams: 2, limit: 6 } })
            .then((r) => { if (!cancelled) setData(r.data); })
            .catch(() => { if (!cancelled) setData(null); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [sinceDays]);

    if (loading) return null;
    if (!data) return null;

    const overlaps = data.overlaps || [];
    if (overlaps.length === 0) {
        // Honest empty state — only render if at least 2 streams have analyses
        if ((data.stream_count_in_scope || 0) < 2 || (data.analyses_in_scope || 0) < 2) return null;
        return (
            <section data-testid="cross-pollination" className="mb-16">
                <div className="flex items-baseline gap-3 mb-4">
                    <div className="overline">Stream cross-pollination</div>
                    <div className="h-px bg-border flex-1" />
                    <span className="font-mono text-[11px] text-muted-foreground tracking-wider">
                        narratives crossing streams
                    </span>
                </div>
                <p className="text-sm text-muted-foreground italic max-w-[60ch]">
                    Each stream is currently telling its own story. Nothing has yet recurred across two or more streams in this window —
                    the narratives are still distinct.
                </p>
            </section>
        );
    }

    // Order by kind for a more editorial layout
    const grouped = data.by_kind || {};

    return (
        <section data-testid="cross-pollination" className="mb-16">
            <div className="flex items-baseline gap-3 mb-7">
                <div className="overline">Stream cross-pollination</div>
                <div className="h-px bg-border flex-1" />
                <span className="font-mono text-[11px] text-muted-foreground tracking-wider">
                    narratives crossing streams
                </span>
                <span className="font-mono text-[11px] text-muted-foreground">
                    {overlaps.length.toString().padStart(2, "0")}
                </span>
            </div>
            <p className="editorial text-[15px] text-muted-foreground leading-relaxed max-w-[68ch] mb-8">
                Where the same language, themes, or tensions are showing up in two or more streams.
                Cross-stream recurrence is a stronger cultural signal than any single stream's story.
            </p>

            <div className="space-y-10">
                {KIND_ORDER.filter((k) => (grouped[k] || []).length > 0).map((kind) => (
                    <div key={kind} data-testid={`cross-poll-group-${kind}`}>
                        <div className="overline text-[10px] mb-4 text-primary">{KIND_LABELS[kind]}</div>
                        <ul className="space-y-4">
                            {(grouped[kind] || []).map((row, i) => (
                                <li
                                    key={`${kind}-${i}`}
                                    data-testid={`cross-poll-item-${kind}-${i}`}
                                    className="grid grid-cols-12 gap-4 border-b border-border pb-4 last:border-b-0 last:pb-0"
                                >
                                    <div className="col-span-12 md:col-span-5">
                                        <div className="flex items-baseline gap-2.5">
                                            <span className="font-mono text-[10px] text-muted-foreground tabular-nums">
                                                {String(i + 1).padStart(2, "0")}
                                            </span>
                                            <span className={`font-display font-semibold leading-tight ${kind === "contradiction" ? "italic text-[16px]" : "text-[17px]"}`}>
                                                {row.term}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="col-span-12 md:col-span-2 flex md:justify-center md:items-center">
                                        <div className="inline-flex items-center gap-1.5 font-mono text-[10px] text-muted-foreground tracking-wider">
                                            <ArrowsHorizontal size={11} weight="bold" className="text-accent" />
                                            <span data-testid={`cross-poll-spread-${kind}-${i}`}>
                                                {row.stream_count} streams
                                            </span>
                                            <span className="text-border">·</span>
                                            <span>{row.total_recurrence}×</span>
                                        </div>
                                    </div>
                                    <div className="col-span-12 md:col-span-5 flex flex-wrap gap-x-2 gap-y-1 md:justify-end">
                                        {row.streams.map((s) => (
                                            <Link
                                                key={s.id}
                                                to={`/streams/${s.id}`}
                                                className="font-mono text-[11px] text-muted-foreground hover:text-primary hover:underline transition-colors"
                                            >
                                                {s.name}
                                                <span className="text-muted-foreground/50"> ·{s.count}</span>
                                            </Link>
                                        ))}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
        </section>
    );
}
